import { EditorPageHeading } from '@components/common/EditorWorkspace'
import ButtonBase from '@mui/material/ButtonBase'
import React, { useState } from 'react'
import Box from '@mui/material/Box'

import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import PollCreateSection from 'src/sections/PollCreateSection'
import CreateListSection from 'src/sections/CreateListSection'
import { List } from 'src/api/types/lists'
import { GlassCard } from '@components/common/Glass'
import { Item } from 'src/components/common/ItemGuessBox'
import { AlertInput } from '@components/common/Alert'
import { AnimatePresence, motion } from 'framer-motion'
import { GameSounds } from 'src/ui/sound/types'

import HowToVoteRoundedIcon from '@mui/icons-material/HowToVoteRounded'
import LiveHelpRoundedIcon from '@mui/icons-material/LiveHelpRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'

export type DashboardCreateTabViewProps = {
  selectedListTab: string
  fullScreen: boolean
  multiMode: string
  gameCode?: string
  hostId?: string
  connectionId?: string
  canCreatePoll: boolean
  mode: 'single' | 'multi' | 'host' | undefined
  userId?: number
  onPollCreated: (pollId: number, pollFormat?: 'ranked_options' | 'community_answers') => void
  onSetSelectedListTab: (tab: string) => void
  onStartCreatedList: (payload: { topic: string; items: Item[]; listId?: number }) => void
  onAlert: (value: AlertInput) => void
  onUiAction?: () => void
  onEditList?: (list: List) => void
  sounds?: GameSounds
}

const DashboardCreateTabView: React.FC<DashboardCreateTabViewProps> = ({
  selectedListTab,
  fullScreen,
  multiMode,
  gameCode,
  hostId,
  connectionId,
  canCreatePoll,
  mode,
  userId,
  onPollCreated,
  onSetSelectedListTab,
  onStartCreatedList,
  onAlert,
  onUiAction,
  onEditList,
  sounds,
}) => {
  const [createMode, setCreateMode] = useState<'trivia' | 'poll'>('trivia')
  const [triviaDraftFromPoll, setTriviaDraftFromPoll] = useState<
    | {
        prompt: string
        items: string[]
        categories?: string[]
        tags?: string[]
        visibility?: 'public' | 'private'
      }
    | undefined
  >(undefined)
  const [pollDraftFromList, setPollDraftFromList] = useState<
    | {
        prompt: string
        items: string[]
        categories?: string[]
        tags?: string[]
        visibility?: 'public' | 'private'
      }
    | undefined
  >(undefined)
  if (selectedListTab !== 'generate') return null

  return (
    <Box data-rt-tab-section px={{ xs: 1.5, sm: 3 }} pt={1} pb={2}>
      <Grid item xs={12} pb={2}>
        <Stack spacing={2} alignItems="stretch">
          <EditorPageHeading
            title="Make something worth ranking."
            description="Turn a great topic into a challenge, a community poll, or a question to share."
          />
          <Box>
            <Box display="flex" gap={2} flexDirection="row" sx={{ width: '100%' }}>
              <ButtonBase
                aria-pressed={createMode === 'trivia'}
                onClick={() => {
                  if (createMode !== 'trivia') {
                    onUiAction?.()
                    setCreateMode('trivia')
                  }
                  setPollDraftFromList(undefined)
                }}
                sx={{
                  p: { xs: 1.5, md: 2 },
                  position: 'relative',
                  flex: 1,
                  minWidth: 0,
                  alignItems: 'stretch',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  cursor: 'pointer',
                  borderRadius: '22px',
                  borderWidth: '2px',
                  borderStyle: 'solid',
                  borderColor: createMode === 'trivia' ? '#b8d8e5' : '#d6e4ef',
                  background: createMode === 'trivia' ? '#eaf6fb' : '#ffffff',
                  color: '#193b58',
                  boxShadow:
                    createMode === 'trivia'
                      ? '0 4px 0 #bfd4e3, 0 8px 18px rgba(28,59,87,0.07)'
                      : '0 3px 0 #e0eaf2',
                  transition:
                    'background-color 160ms ease, transform 160ms ease, box-shadow 160ms ease',
                  '@media (hover: hover) and (pointer: fine)': {
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      background: createMode === 'trivia' ? '#e4f3f9' : '#f7fbfe',
                    },
                  },
                }}
              >
                {createMode === 'trivia' && (
                  <CheckRoundedIcon
                    sx={{
                      position: 'absolute',
                      right: 10,
                      top: 10,
                      fontSize: 16,
                      color: '#087fa2',
                    }}
                  />
                )}
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  justifyContent="center"
                  mb={0.5}
                  sx={{
                    color: '#193b58',
                  }}
                >
                  <LiveHelpRoundedIcon sx={{ fontSize: 24, color: '#087fa2' }} />
                  <Typography variant="h6" fontWeight={800} color="#193b58">
                    Trivia
                  </Typography>
                </Box>
                <Typography variant="body2" color="#60768b" mb={1.5}>
                  Facts in the right order.
                </Typography>
              </ButtonBase>
              <ButtonBase
                aria-pressed={createMode === 'poll'}
                disabled={!canCreatePoll}
                onClick={() => {
                  if (!canCreatePoll) return
                  if (createMode !== 'poll') {
                    onUiAction?.()
                    setCreateMode('poll')
                  }
                  setTriviaDraftFromPoll(undefined)
                }}
                sx={{
                  p: { xs: 1.5, md: 2 },
                  position: 'relative',
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  cursor: canCreatePoll ? 'pointer' : 'not-allowed',
                  borderRadius: '22px',
                  borderWidth: '2px',
                  borderStyle: 'solid',
                  borderColor: createMode === 'poll' ? '#e6c5a8' : '#efddc9',
                  background: createMode === 'poll' ? '#fff4e8' : '#ffffff',
                  color: '#944217',
                  boxShadow:
                    createMode === 'poll'
                      ? '0 4px 0 #e8bb99, 0 8px 18px rgba(28,59,87,0.07)'
                      : '0 3px 0 #e8cdb2',
                  transition:
                    'background-color 160ms ease, transform 160ms ease, box-shadow 160ms ease',
                  '@media (hover: hover) and (pointer: fine)': {
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      background: createMode === 'poll' ? '#fff0df' : '#fffbf6',
                    },
                  },
                  opacity: canCreatePoll ? 1 : 0.7,
                }}
              >
                {createMode === 'poll' && (
                  <CheckRoundedIcon
                    sx={{
                      position: 'absolute',
                      right: 10,
                      top: 10,
                      fontSize: 16,
                      color: '#944217',
                    }}
                  />
                )}
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  justifyContent="center"
                  mb={0.5}
                  sx={{
                    color: '#944217',
                  }}
                >
                  <HowToVoteRoundedIcon sx={{ fontSize: 24, color: '#b56835' }} />
                  <Typography variant="h6" fontWeight={800} color="#944217">
                    Poll
                  </Typography>
                </Box>
                <Typography variant="body2" color="#855c3d" mb={canCreatePoll ? 1 : 0}>
                  Let the community decide.
                </Typography>

                {!canCreatePoll ? (
                  <Typography variant="body2" sx={{ fontWeight: 500 }} mt={1}>
                    Log in to create polls
                  </Typography>
                ) : null}
              </ButtonBase>
            </Box>
          </Box>
        </Stack>
      </Grid>

      <AnimatePresence mode="wait" initial={false}>
        {createMode === 'poll' ? (
          <motion.div
            key="create-poll"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.16, ease: 'easeOut' }}
          >
            {canCreatePoll ? (
              <PollCreateSection
                showHeader={false}
                initialDraft={pollDraftFromList}
                onConvertToTrivia={(draft) => {
                  onUiAction?.()
                  setTriviaDraftFromPoll(draft)
                  setPollDraftFromList(undefined)
                  setCreateMode('trivia')
                }}
                onCreated={(pollId, pollFormat) => {
                  setPollDraftFromList(undefined)
                  onSetSelectedListTab('polls')
                  onPollCreated(pollId, pollFormat)
                }}
                sounds={sounds}
                onAlert={onAlert}
              />
            ) : (
              <GlassCard
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: '24px',
                  background: '#e1f5fb',
                  borderColor: 'rgba(148,163,184,0.4)',
                }}
              >
                <Typography variant="h6" fontWeight={600} mb={0.5}>
                  Log in to create polls
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Polls are tied to your account so you can manage and share them.
                </Typography>
              </GlassCard>
            )}
          </motion.div>
        ) : null}

        {createMode === 'trivia' ? (
          <motion.div
            key="create-trivia"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.16, ease: 'easeOut' }}
          >
            <CreateListSection
              fullScreen={fullScreen}
              multiMode={multiMode}
              gameCode={gameCode}
              hostId={hostId ? `${hostId}` : undefined}
              connectionId={connectionId}
              mode={mode}
              userId={userId}
              onStartGame={onStartCreatedList}
              initialDraft={triviaDraftFromPoll}
              onAlert={onAlert}
              onEditList={onEditList}
              onConvertToPoll={(draft) => {
                onUiAction?.()
                setPollDraftFromList(draft)
                setTriviaDraftFromPoll(undefined)
                setCreateMode('poll')
              }}
              sounds={sounds}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </Box>
  )
}

export default DashboardCreateTabView
